import { Box, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import {
  DataGrid,
  GridCsvExportMenuItem,
  GridToolbarContainer,
  GridToolbarExportContainer,
} from '@mui/x-data-grid';
import { FormatCurrency } from '../../utils/numbers';
import { formatChileanDate } from '../../utils/utils';
import Spinner from '../Spinner';

const csvOptions = { delimiter: ';' };

function CustomExportButton(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem rintOptions={{ fileName: 'Descargar informe' }} options={csvOptions} />
    </GridToolbarExportContainer>
  );
}

function CustomToolbar(props) {
  return (
    <GridToolbarContainer {...props}>
      <CustomExportButton />
    </GridToolbarContainer>
  );
}

function NoRowsOverlay() {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      <Typography sx={{ fontSize: '16px', textAlign: 'center' }}>
        No hay transacciones para mostrar
      </Typography>
    </Stack>
  );
}

const TabletGridWallet = ({
  sortedArrayDateStatusTransactions,
  loadingTransactions,
  errorTransactions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const column = [
    {
      field: 'tradeDate',
      headerName: 'Fecha',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      type: 'date',
      renderCell: params => (
        <Typography variant='subtitle2' sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
          {formatChileanDate(params.value)}
        </Typography>
      ),
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('es-CL');
      },
    },
    {
      field: 'subTypeDetail',
      headerName: 'Movimientos',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'super-app-theme--cell',
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      align: 'right',
      headerAlign: 'center',
      renderCell: params => {
        const value = params.value;
        return (
          <Typography
            variant='subtitle2'
            sx={{
              pr: 4,
              width: '100%',
              textAlign: 'right',
              fontSize: isMobile ? '0.75rem' : '1rem',
            }}>
            {value === '' || value === null || value === undefined
              ? '----------'
              : FormatCurrency(value)}
          </Typography>
        );
      },
      valueFormatter: params => {
        if (!params.value) return '';
        const number = Number(params.value.toString().replace(/\./g, ''));
        return `$ ${number.toLocaleString('de-DE')}`;
      },
      sortComparator: (v1, v2) => v1 - v2,
    },
  ];
  const transactions = sortedArrayDateStatusTransactions?.map(transaction => {
    let amountValue;

    if (transaction.subType === 'WTHD') {
      amountValue = -Math.abs(transaction.amount);
    } else {
      amountValue = Math.abs(transaction.amount);
    }

    return {
      accountId: transaction.accountId,
      amount: amountValue,
      id: transaction.id,
      note: transaction.note,
      price: transaction.price,
      securityId: transaction.securityId,
      securityName: transaction.securityName,
      status: transaction.status,
      subType: transaction.subType,
      subTypeDetail: transaction.subTypeDetail,
      tradeDate: transaction.tradeDate,
      units: transaction.units,
    };
  });

  if (loadingTransactions) {
    return <Spinner />;
  }

  if (errorTransactions) {
    throw new Error('Error component wallet');
  }

  return (
    <Box
      sx={{
        height: '400px',
        width: { md: '100%' },
        '& .font-tabular-nums': {
          fontVariantNumeric: 'tabular-nums',
        },
      }}>
      <DataGrid
        rowHeight={50}
        columns={column}
        rows={transactions}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }
        {...transactions}
        initialState={{
          ...transactions.initialState,
          pagination: {
            ...transactions.initialState?.pagination,
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        sx={{
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.white.main,
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.smallText.fontSize,
            },
          },
          '.MuiDataGrid-toolbarContaine, .css-128fb87-MuiDataGrid-toolbarContainer': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
          },
          '.MuiDataGrid-toolbarContainer>button': {
            color: '#475569',
            backgroundColor: theme.palette.white.main,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          },
          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '&.MuiDataGrid-root': {
            borderRadius: '8px',
          },
          '.MuiDataGrid-row': {
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.smallText.fontSize,
            },
            textAlign: 'left',
            justifyContent: 'center',
          },
          '& .super-app-theme--cell': {
            width: '100%',
            color: theme.palette.primary.main,
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '.MuiDataGrid-footerContainer': {
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.smallText.fontSize,
            },
            color: '#ddd',
          },
          '.MuiButtonBase-root': {
            color: theme.palette.white.main,
          },
          '.MuiDataGrid-footerContainer  .MuiButtonBase-root ': {
            color: theme.palette.primary.main,
            width: { xs: '30px' },
            '&.Mui-disabled': {
              color: '#6c6c6c94',
            },
          },
          '& .even-row': {
            backgroundColor: theme.palette.primary.textLightBlue,
          },
          '& .odd-row': {
            backgroundColor: theme.palette.primary.blueSkyInput,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        localeText={{
          toolbarDensity: 'Size',
          toolbarDensityLabel: 'Size',
          toolbarDensityCompact: 'Small',
          toolbarDensityStandard: 'Medium',
          toolbarDensityComfortable: 'Large',
          toolbarExportCSV: 'Exportar a CSV',
          toolbarExport: 'EXPORTAR',
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          pagination: { labelRowsPerPage: 'Filas por página' },
        }}
      />
    </Box>
  );
};

export default TabletGridWallet;
